import { CheckCircleIcon, CalendarIcon, ClockIcon, UserIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

export interface BookingConfirmationProps {
  booking: {
    id: number;
    slotDate: string;
    slotTime: string;
    sessionType: string;
    amountCents: number;
  };
  therapist: {
    id: number;
    firstName: string;
    lastName: string;
    profilePhoto?: string;
  };
}

/**
 * BookingConfirmation component displayed after successful booking
 * Shows booking details and provides actions like adding to calendar
 */
export function BookingConfirmation({ booking, therapist }: BookingConfirmationProps) {
  const formatCurrency = (cents: number): string => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (timeStr: string): string => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const handleAddToCalendar = () => {
    // Create ICS file for calendar
    const startDateTime = new Date(`${booking.slotDate}T${booking.slotTime}`);
    const endDateTime = new Date(startDateTime.getTime() + 60 * 60 * 1000); // 1 hour session

    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'BEGIN:VEVENT',
      `DTSTART:${startDateTime.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`,
      `DTEND:${endDateTime.toISOString().replace(/[-:]/g, '').split('.')[0]}Z`,
      `SUMMARY:Therapy Session with ${therapist.firstName} ${therapist.lastName}`,
      `DESCRIPTION:${booking.sessionType} therapy session`,
      'STATUS:CONFIRMED',
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\n');

    const blob = new Blob([icsContent], { type: 'text/calendar' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'therapy-session.ics';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Success Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
          <CheckCircleIcon className="h-10 w-10 text-green-600" />
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          Booking Confirmed!
        </h2>
        <p className="text-lg text-gray-600">
          Your therapy session has been successfully booked
        </p>
      </div>

      {/* Booking Details Card */}
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden mb-6">
        <div className="bg-indigo-600 px-6 py-4">
          <h3 className="text-lg font-semibold text-white">Session Details</h3>
        </div>

        <div className="p-6 space-y-6">
          {/* Therapist Info */}
          <div className="flex items-center gap-4 pb-6 border-b border-gray-200">
            {therapist.profilePhoto ? (
              <img
                src={therapist.profilePhoto}
                alt={`${therapist.firstName} ${therapist.lastName}`}
                className="w-16 h-16 rounded-full object-cover"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center">
                <UserIcon className="h-8 w-8 text-indigo-600" />
              </div>
            )}
            <div>
              <p className="text-sm text-gray-500">Your Therapist</p>
              <p className="text-lg font-semibold text-gray-900">
                {therapist.firstName} {therapist.lastName}
              </p>
            </div>
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <CalendarIcon className="h-6 w-6 text-indigo-600 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500">Date</p>
                <p className="text-base font-medium text-gray-900">
                  {formatDate(booking.slotDate)}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <ClockIcon className="h-6 w-6 text-indigo-600 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500">Time</p>
                <p className="text-base font-medium text-gray-900">
                  {formatTime(booking.slotTime)}
                </p>
              </div>
            </div>
          </div>

          {/* Session Type and Amount */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
            <div>
              <p className="text-sm text-gray-500">Session Type</p>
              <p className="text-base font-medium text-gray-900">
                {booking.sessionType === 'INTAKE'
                  ? 'Initial Consultation'
                  : booking.sessionType === 'REGULAR'
                  ? 'Regular Session'
                  : 'Crisis Session'}
              </p>
            </div>

            <div>
              <p className="text-sm text-gray-500">Amount Paid</p>
              <p className="text-base font-medium text-gray-900">
                {formatCurrency(booking.amountCents)}
              </p>
            </div>
          </div>

          {/* Booking ID */}
          <div className="pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500">Booking ID: #{booking.id}</p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-4">
        <button
          onClick={handleAddToCalendar}
          className="flex-1 px-6 py-3 bg-white border-2 border-indigo-600 text-indigo-600 font-semibold rounded-lg hover:bg-indigo-50 transition-colors"
        >
          Add to Calendar
        </button>

        <Link
          to={`/bookings/${booking.id}`}
          className="flex-1 px-6 py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors text-center"
        >
          View Booking
        </Link>
      </div>

      {/* Next Steps */}
      <div className="mt-8 p-6 bg-blue-50 border border-blue-100 rounded-lg">
        <h4 className="font-semibold text-blue-900 mb-2">What's Next?</h4>
        <ul className="space-y-2 text-sm text-blue-800">
          <li className="flex items-start gap-2">
            <span className="text-blue-600 mt-0.5">•</span>
            <span>
              You'll receive a confirmation email with session details and a link to join
              the video call.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 mt-0.5">•</span>
            <span>
              Log in 5 minutes before your appointment to test your audio and video.
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 mt-0.5">•</span>
            <span>
              If you need to cancel or reschedule, please do so at least 24 hours in
              advance for a full refund.
            </span>
          </li>
        </ul>
      </div>

      {/* Return Home */}
      <div className="mt-6 text-center">
        <Link
          to="/"
          className="text-indigo-600 hover:text-indigo-700 font-medium text-sm"
        >
          Return to Home
        </Link>
      </div>
    </div>
  );
}
